// Constants as in the backend. Is used to set the project mode
export const IMAGE_MODE = Object.freeze({
	GENERATE_ORTHOPHOTO: 'generate_orthophoto',
	UPLOAD_ORTHOPHOTO: 'upload_orthophoto',
	SINGLE_IMAGE: 'single_image',
	VIDEO: 'video',
});

// Constants as in the frontend for the project mode
export const PROJECT_MODE = Object.freeze({
	ORTHOPHOTO: 'orthophoto',
	SINGLE_IMAGE: 'single_image',
	VIDEO: 'video',
});

// User-facing project-type names for PROJECT_MODE values.
export const PROJECT_TYPE_LABELS = Object.freeze({
	[PROJECT_MODE.ORTHOPHOTO]: 'Orthophoto',
	[PROJECT_MODE.SINGLE_IMAGE]: 'Single image',
});

// Constants for the model mode
export const MODEL_TYPE = Object.freeze({
	OBJECT_DETECTION: 'object_detection',
	SEGMENTATION: 'segmentation',
});

// Constants for the coordinate system
export const COORDINATE_SYSTEM = Object.freeze({
	PIXEL: 'pixel',
	EPSG_3857: 'EPSG:3857',
});

// Object Picker collection type (UI classification, mapped from the backend
// `scope`: system → template, org → org, user → personal).
export const COLLECTION_TYPE = Object.freeze({
	TEMPLATE: 'template',
	ORG: 'org',
	PERSONAL: 'personal',
});

// Object Picker: how the detection run is scoped (Area step). `null` = unset.
export const AREA_MODE = Object.freeze({
	DRAW: 'draw', // detect within a drawn area of interest
	ENTIRE: 'entire', // detect across the entire project
});

// Backend job lifecycle statuses (e.g. detection jobs).
export const JOB_STATUS = Object.freeze({
	PENDING: 'pending',
	RUNNING: 'running',
	FINISHED: 'finished',
	FAILED: 'failed',
	KILLED: 'killed',
	CANCELLED: 'cancelled',
});

// Statuses that mean a job has stopped (successfully or not) — polling ends here.
export const TERMINAL_JOB_STATUSES = Object.freeze([
	JOB_STATUS.FINISHED,
	JOB_STATUS.FAILED,
	JOB_STATUS.KILLED,
	JOB_STATUS.CANCELLED,
]);

// Terminal statuses that represent a failed/aborted run (not a success).
export const FAILED_JOB_STATUSES = Object.freeze([
	JOB_STATUS.FAILED,
	JOB_STATUS.KILLED,
	JOB_STATUS.CANCELLED,
]);

// Backend job types (from the jobs API `job_type` field).
export const JOB_TYPE = Object.freeze({
	OBJECT_DETECTION_PREDICT: 'object_detection_predict',
});

// `customLayerId` of every map layer belonging to the Collection Results feature.
// Single source of truth: the layer components register with these ids, and other
// features (e.g. the AOI draw flow) address the layers by them.
export const COLLECTION_LAYER_ID = Object.freeze({
	CENTER: 'collection-center',
	OUTLINE: 'collection-outline',
	BOUNDING_BOX: 'collection-bounding-box',
	SINGLE_IMAGE: 'collectionSingleImageLayer',
	HEXAGON: 'collectionHexagonLayer',
	HEXAGON_SELECTED: 'collectionHexagonSelectedLayer',
	DSP: 'collectionDspLayer',
	BOUNDARY: 'collectionBoundaryLayer',
});

// Everything the selected collection draws on the map — detections in all three
// shapes, the aggregations, and the scanned-area boundary. Used by flows that
// need the analyzed results out of the way (sketching an area of interest), where
// leaving any of them on the map competes with the polygon being drawn.
export const COLLECTION_RESULT_LAYER_IDS = Object.freeze([
	COLLECTION_LAYER_ID.CENTER,
	COLLECTION_LAYER_ID.OUTLINE,
	COLLECTION_LAYER_ID.BOUNDING_BOX,
	COLLECTION_LAYER_ID.SINGLE_IMAGE,
	COLLECTION_LAYER_ID.HEXAGON,
	COLLECTION_LAYER_ID.HEXAGON_SELECTED,
	COLLECTION_LAYER_ID.DSP,
	COLLECTION_LAYER_ID.BOUNDARY,
]);

export const MAP_LAYER_NAMES = Object.freeze({
	orthophoto: 'Aerial Map',
	canopy_height: 'Height Model',
	colored_chm: 'Height Classes',
	national_dtm: 'Terrain',
	contours: 'Contours',
	raw_images: 'Drone Images',
});

// Org products that can be toggled on/off (mirrors the backend ProductEnum).
// Each product enables a set of entitlements server-side when turned on.
// `value` is the ProductEnum sent in the API path; `label` is for display.
// `isDefault` marks products that are part of every org's baseline set and so
// are not shown as toggles in the admin UI.
export const PRODUCTS = Object.freeze([
	{ value: 'product.portal', label: 'Portal', isDefault: true },
	{ value: 'product.training', label: 'Training', isDefault: true },
	{ value: 'product.dsp', label: 'DSP' },
	{ value: 'product.wms', label: 'WMS' },
	{ value: 'product.flightplanner', label: 'Flight Planner' },
	{ value: 'product.video', label: 'Video' },
	{ value: 'product.hip', label: 'HIP' },
]);

// Rendering mode derived from the project route (/projects/:id vs /projects/v2/:id).
// Not to be confused with PROJECT_MODE above (image_mode: orthophoto vs single-image).
// URL segment is 'v2', not 'collection' - avoids colliding with the unrelated
// ?collection_uuid= query param (CollectionPicker's picked detection-collection template).
export const PROJECT_VIEW_MODE = Object.freeze({
	MODEL: 'model',
	COLLECTION: 'collection',
});

// Single source of truth for which mode project surfaces (cards, lists, tables)
// link to by default. Callers that need the non-default mode still reference
// PROJECT_VIEW_MODE.MODEL/COLLECTION directly.
export const DEFAULT_PROJECT_VIEW_MODE = PROJECT_VIEW_MODE.COLLECTION;

// Kill switch for the model-based view. Everything that knows model mode exists
// (the in-page view switch, the "View in model-based mode" list items, the
// /projects/:id route branch) reads this, so retiring model mode is a single
// flip here; the legacy components can then be deleted at leisure.
// Prerequisite before flipping: annotation must exist in the collection flow.
export const MODEL_VIEW_MODE_ENABLED = true;

/**
 * Preset colours offered wherever an object gets a colour: the object form,
 * collection object styling and the hexagon layer's picker.
 */
export const OBJECT_COLOR_PALETTE = Object.freeze([
	'#E85D9C',
	'#F57C00',
	'#FFB300',
	'#7CB342',
	'#4caf50',
	'#26A69A',
	'#29B6F6',
	'#5C6BC0',
	'#AB47BC',
	'#78909C',
]);
